import React, { useContext, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp, FiFolder, FiCalendar } = FiIcons;

// Helper: Conta il totale dei media (inclusi quelli nelle sottosezioni)
const getMediaCount = (section) => {
  let count = (section.items || []).length;
  if (section.subSections) {
    for (let sub of section.subSections) {
      count += getMediaCount(sub);
    }
  }
  return count;
};

// Componente: Singola Immagine
const MediaItem = ({ m, allMatches }) => {
  const match = m.activity_id ? allMatches.find(a => a.id === m.activity_id) : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-nba-card border border-white/5 hover:border-white/20 transition-all group flex flex-col overflow-hidden shadow-lg"
    >
      <a href={m.url} target="_blank" rel="noopener noreferrer" className="block w-full bg-nba-dark overflow-hidden">
        <img
          src={m.url}
          alt="Gallery Media"
          className="w-full h-auto object-contain opacity-70 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </a>

      {/* Informazioni Evento Discrete (Stile Schedule) */}
      {match && (
        <div className="p-4 bg-black/40 border-t border-white/5 flex flex-col gap-1.5 shrink-0">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.2em] text-white/30">
            <span className="flex items-center gap-1.5">
              <SafeIcon icon={FiCalendar} className="shrink-0" /> {match.time || 'Evento'}
            </span>
            {match.status === 'live' && (
              <span className="text-red-500 font-bold tracking-[0.2em] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span> LIVE
              </span>
            )}
          </div>
          <span className="font-league text-2xl uppercase italic tracking-tight text-white/80 group-hover:text-white transition-colors truncate leading-none mt-1">
            {match.title}
          </span>
        </div>
      )}
    </motion.div>
  );
};

// Componente: Card della Sezione (Accordion)
const FolderCard = ({ section, allMatches, level = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const count = getMediaCount(section);

  return (
    <div className={`w-full ${level > 0 ? 'mt-3 pl-4 md:pl-8 border-l border-white/10' : 'mt-6'}`}>
      {/* Intestazione Accordion (Stile Schedule Minimale) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 bg-nba-card border border-white/5 hover:bg-white/5 transition-colors group outline-none relative overflow-hidden"
      >
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-white/10 group-hover:bg-white/30 transition-colors" />
        <div className="flex items-center gap-5 text-left ml-2">
          <SafeIcon icon={FiFolder} className="text-2xl text-white/20 group-hover:text-white/40 transition-colors shrink-0" />
          <div className="flex flex-col">
            <h3 className={`font-league uppercase italic tracking-tight leading-none transition-colors ${level === 0 ? 'text-4xl text-white group-hover:text-white/80' : 'text-3xl text-white/80 group-hover:text-white'}`}>
              {section.title}
            </h3>
            <div className="flex items-center gap-2 text-white/40 text-[10px] font-black uppercase tracking-widest mt-1.5">
              <span>{count} Media</span>
              {section.subSections && section.subSections.length > 0 && (
                <>
                  <span className="text-white/20">•</span>
                  <span>{section.subSections.length} Sezioni</span>
                </>
              )}
            </div>
          </div>
        </div>
        <SafeIcon icon={isOpen ? FiChevronUp : FiChevronDown} className="text-2xl text-white/40 group-hover:text-white transition-colors shrink-0" />
      </button>

      {/* Contenuto Espanso */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-5 pb-2">
              {/* Sottosezioni Nidificate */}
              {section.subSections && section.subSections.length > 0 && (
                <div className="space-y-3 mb-6">
                  {section.subSections.map(sub => (
                    <FolderCard key={sub.id} section={sub} allMatches={allMatches} level={level + 1} />
                  ))}
                </div>
              )}

              {/* Media Diretti */}
              {section.items && section.items.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-start">
                  {section.items.map(m => (
                    <MediaItem key={m.id} m={m} allMatches={allMatches} />
                  ))}
                </div>
              )}

              {/* Messaggio Sezione Vuota */}
              {(!section.subSections?.length && !section.items?.length) && (
                <div className="py-8 text-center text-white/30 font-league text-2xl uppercase italic border border-white/5 bg-black/20">
                  Nessun elemento in questa sezione.
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Gallery = () => {
  const { schedule, gallery, gallerySections, textStyles } = useContext(AppContext);
  const allMatches = Object.values(schedule || {}).flat();

  // Costruzione dell'albero gerarchico memorizzata
  const tree = useMemo(() => {
    const sectionMap = {};

    // 1. Inizializza dalle sezioni salvate nel JSON (config_v1)
    (gallerySections || []).forEach(s => {
      if (s.visible !== false) {
        sectionMap[s.id] = { ...s, items: [], subSections: [] };
      }
    });

    const uncategorizedMedia = [];

    // 2. Popola con i media da Supabase (gallery_v1)
    (gallery || []).forEach(m => {
      // Filtraggio esplicito tramite la colonna section_visible di gallery_v1
      if (m.section_visible === false) return;

      let placed = false;

      // Utilizzo di parentId: posiziona il media nella sezione o sottosezione corretta
      if (m.parentId) {
        if (sectionMap[m.parentId]) {
          sectionMap[m.parentId].items.push(m);
          placed = true;
        } else {
          // Ricostruzione dinamica: se la sezione manca dal JSON ma i dati ci sono in gallery_v1, la ricreiamo
          sectionMap[m.parentId] = {
            id: m.parentId,
            title: m.section_title || 'Sezione Recuperata',
            order: m.section_order || 999, // Utilizza section_order da gallery_v1
            visible: true,
            items: [m],
            subSections: []
          };
          placed = true;
        }
      } 
      // Utilizzo di section_title come fallback per media legacy (senza parentId)
      else if (m.section_title) {
        const legacySec = Object.values(sectionMap).find(s => s.title === m.section_title);
        if (legacySec) {
          legacySec.items.push(m);
          placed = true;
        } else {
          // Creazione sezione legacy on-the-fly basata su section_title e section_order
          const newId = 'legacy_' + m.section_title.replace(/\s+/g, '_');
          sectionMap[newId] = {
            id: newId,
            title: m.section_title,
            order: m.section_order || 999, // Utilizza section_order da gallery_v1
            visible: true,
            items: [m],
            subSections: []
          };
          placed = true;
        }
      }

      // Se non ricade in nessuna casistica, va nei non categorizzati
      if (!placed) {
        uncategorizedMedia.push(m);
      }
    });

    // 3. Costruzione dell'albero (Relazioni Padre-Figlio)
    const builtTree = [];
    Object.values(sectionMap).forEach(sec => {
      // Se la sezione ha a sua volta un padre (è una sottosezione), la inseriamo nel padre
      if (sec.parentId && sectionMap[sec.parentId]) {
        sectionMap[sec.parentId].subSections.push(sec);
      } else {
        // Altrimenti è una sezione principale
        builtTree.push(sec);
      }
    });

    // 4. Ordinamento basato su order (che mappa anche section_order di gallery_v1)
    builtTree.forEach(main => {
      main.subSections.sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    builtTree.sort((a, b) => (a.order || 0) - (b.order || 0));

    // Aggiunta coda "Altri Media"
    if (uncategorizedMedia.length > 0) {
      builtTree.push({
        id: 'uncategorized',
        title: 'Altri Media',
        order: 99999,
        items: uncategorizedMedia,
        subSections: []
      });
    }

    return builtTree;
  }, [gallery, gallerySections]);

  return (
    <div className="py-12 space-y-10 max-w-7xl mx-auto px-4 min-h-[75vh]">
      {/* Header */}
      <div className="border-b-4 border-white pb-6 space-y-6">
        <h1 className="font-league italic leading-none tracking-tighter uppercase dyn-galleryTitle">
          {textStyles.galleryTitle?.text}
        </h1>
      </div>

      {/* Contenuto ad Albero (Accordion) */}
      <div className="space-y-4">
        {tree.length > 0 ? (
          tree.map(sec => (
            <FolderCard key={sec.id} section={sec} allMatches={allMatches} level={0} />
          ))
        ) : (
          <div className="py-20 text-center text-white/20 font-league text-4xl uppercase italic">
            Nessun media o sezione presente.
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;